import { ReactNode, useEffect, useState } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router'

import { ConfirmDialogHost } from '@/components/shared/confirm-dialog'
import { DebugNetworkOverlay } from '@/components/shared/debug-network-overlay'
import { ToastHistoryRecorder } from '@/components/shared/toast-history/toast-history-recorder'
import { ThemeProvider } from '@/components/theme-provider'
import { Toaster } from '@/components/ui/sonner'
import { Spinner } from '@/components/ui/spinner'
import { useAuthSync } from '@/hooks/useAuthSync'
import { useOfflineAuth } from '@/hooks/useOfflineAuth'
import { usePasskeyNudge } from '@/hooks/usePasskeyNudge'
import { useRouteRestore } from '@/hooks/useRouteRestore'
import { useSseSync } from '@/hooks/useSseSync'
import { useSyncEngine } from '@/hooks/useSyncEngine'
import { PATHS } from '@/routes/paths'
import { useAppStore, useAppStorePersisted } from '@/store/useAppStore'
import { useAuthStore, useIsSignedIn } from '@/store/useAuthStore'

import { ActivityPage } from './activity'
import { HaDevices } from './admin/ha-devices'
import { AdminUsers } from './admin/users'
import { SignInPage } from './auth/sign-in'
import { SignOut } from './auth/sign-out'
import { ContentsCheck } from './contents-check'
import { CustomMusic } from './custom-music'
import { CustomVideo } from './custom-video'
import { EufyPage } from './eufy'
import { FlashcardReviewPage } from './flashcard-review'
import { GrammarCheck } from './grammar-check'
import { Home } from './home'
import { ImportPage } from './import'
import { LibrarySearchPage } from './library-search'
import { MapShell } from './map'
import { MemoPage } from './memo'
import { BookNotePage } from './memo/book-note'
import { LinkGraphPage } from './memo/graph'
import { MusicLibraryPage } from './music'
import { MusicAlbumDetailPage } from './music/album-detail'
import { MusicArtistDetailPage } from './music/artist-detail'
import { MusicArtistsPage } from './music/artists'
import { MusicDownloadedPage } from './music/downloaded'
import { MusicLayout } from './music/layout'
import { MusicPlaylistDetailPage } from './music/playlist-detail'
import { MusicPlaylistsPage } from './music/playlists'
import { MusicSongsPage } from './music/songs'
import { NotificationsPage } from './notifications'
import { NutritionPage } from './nutrition'
import { OfflineProducts } from './offline-products'
import { PlacesMapPage } from './places-map'
import { ComicReader } from './products/comic-reader'
import { ProductContainer } from './products/product-container'
import { RallyImportPage } from './rallies/import'
import { ReceiptListPage } from './receipts'
import { RoutePlanCreatePage } from './route-plans/create'
import { SettingsAiUsagesPage } from './settings/ai-usages'
import { SettingsApiUsagePage } from './settings/api-usage'
import { SettingsEufyPage } from './settings/eufy'
import { SettingsIntegrationsPage } from './settings/integrations'
import { SettingsPasskeysPage } from './settings/passkeys'
import { SharePage } from './share'
import { SleepReportPage } from './sleep'
import { StorageManagement } from './storage-management'
import { TodoPage } from './todo'
import { TripsPage } from './trips'
import { TripDetailPage } from './trips/detail'
import { TripHighlightPage } from './trips/highlight'
import { WorkoutHomePage } from './workout'
import { SessionRunnerPage } from './workout/components/session-runner'

// ルート設定
type RouteConfig = {
  path: string
  element: ReactNode
}

const routes: RouteConfig[] = [
  { path: PATHS.HOME, element: <Home /> },
  // 新しいパス構造
  { path: PATHS.P.LIST, element: <ProductContainer /> },
  { path: PATHS.P.EPISODE, element: <ComicReader /> },
  // 独立パス
  { path: PATHS.CHECK, element: <ContentsCheck /> },
  // その他のページ
  { path: PATHS.CUSTOM_VIDEO, element: <CustomVideo /> },
  { path: PATHS.CUSTOM_MUSIC, element: <CustomMusic /> },
  { path: PATHS.STORAGE_MANAGEMENT, element: <StorageManagement /> },
  { path: PATHS.GRAMMAR_CHECK, element: <GrammarCheck /> },
  { path: PATHS.ACTIVITY, element: <ActivityPage /> },
  { path: PATHS.PLACES, element: <PlacesMapPage /> },
  { path: PATHS.TRIPS.LIST, element: <TripsPage /> },
  { path: PATHS.TRIPS.DETAIL, element: <TripDetailPage /> },
  { path: PATHS.TRIPS.HIGHLIGHT, element: <TripHighlightPage /> },
  { path: PATHS.RALLIES.IMPORT, element: <RallyImportPage /> },
  { path: PATHS.ROUTE_PLANS.CREATE, element: <RoutePlanCreatePage /> },
  { path: PATHS.ADMIN.USERS, element: <AdminUsers /> },
  { path: PATHS.SHARE, element: <SharePage /> },
  { path: PATHS.OFFLINE_PRODUCTS, element: <OfflineProducts /> },
  { path: PATHS.WORKOUT.HOME, element: <WorkoutHomePage /> },
  { path: PATHS.WORKOUT.SESSION, element: <SessionRunnerPage /> },
  { path: PATHS.NUTRITION.HOME, element: <NutritionPage /> },
  { path: PATHS.SETTINGS.INTEGRATIONS, element: <SettingsIntegrationsPage /> },
  { path: PATHS.SETTINGS.EUFY, element: <SettingsEufyPage /> },
  { path: PATHS.SETTINGS.HA, element: <HaDevices /> },
  { path: PATHS.SETTINGS.API_USAGE, element: <SettingsApiUsagePage /> },
  { path: PATHS.SETTINGS.AI_USAGES, element: <SettingsAiUsagesPage /> },
  { path: PATHS.SETTINGS.PASSKEYS, element: <SettingsPasskeysPage /> },
  { path: PATHS.EUFY.HOME, element: <EufyPage /> },
  { path: PATHS.SLEEP.HOME, element: <SleepReportPage /> },
  { path: PATHS.TODO.HOME, element: <TodoPage /> },
  { path: PATHS.FLASHCARD.REVIEW, element: <FlashcardReviewPage /> },
  { path: PATHS.MEMO.LIST, element: <MemoPage /> },
  { path: PATHS.MEMO.BOOK_NOTE, element: <BookNotePage /> },
  { path: PATHS.MEMO.GRAPH, element: <LinkGraphPage /> },
  { path: PATHS.LIBRARY.SEARCH, element: <LibrarySearchPage /> },
  { path: PATHS.RECEIPTS.LIST, element: <ReceiptListPage /> },
  { path: PATHS.IMPORT, element: <ImportPage /> },
  { path: PATHS.NOTIFICATIONS, element: <NotificationsPage /> },
  { path: PATHS.SIGN_OUT, element: <SignOut /> },
]

// MusicLayout 配下でのみ描画するルート。この配列が「音楽画面の範囲」の定義そのものになる
const musicRoutes: RouteConfig[] = [
  { path: PATHS.MUSIC.LIBRARY, element: <MusicLibraryPage /> },
  { path: PATHS.MUSIC.ARTISTS, element: <MusicArtistsPage /> },
  { path: PATHS.MUSIC.ARTIST_DETAIL, element: <MusicArtistDetailPage /> },
  { path: PATHS.MUSIC.ALBUM_DETAIL, element: <MusicAlbumDetailPage /> },
  { path: PATHS.MUSIC.SONGS, element: <MusicSongsPage /> },
  { path: PATHS.MUSIC.PLAYLISTS, element: <MusicPlaylistsPage /> },
  { path: PATHS.MUSIC.PLAYLIST_DETAIL, element: <MusicPlaylistDetailPage /> },
  { path: PATHS.MUSIC.DOWNLOADED, element: <MusicDownloadedPage /> },
]

// MapShell 配下で描画するルート。1 つの地図を使い回すので、画面ごとの要素は持たない
const mapRoutes: string[] = [PATHS.RALLIES.LIST, PATHS.RALLIES.DETAIL, PATHS.ROUTE_PLANS.LIST, PATHS.ROUTE_PLANS.DETAIL]

const AppRoutes = ({ routes }: { routes: RouteConfig[] }) => (
  <Routes>
    {routes.map(({ path, element }) => (
      <Route key={path} path={path} element={element} />
    ))}
    {/* ラリーとルートは 1 つの地図を使い回す。URL を消費しないレイアウトで束ね、
        画面を移ってもシェルが作り直されないようにする */}
    <Route element={<MapShell />}>
      {mapRoutes.map((path) => (
        <Route key={path} path={path} element={null} />
      ))}
    </Route>
    <Route element={<MusicLayout />}>
      {musicRoutes.map(({ path, element }) => (
        <Route key={path} path={path} element={element} />
      ))}
    </Route>
  </Routes>
)

// ローディング画面コンポーネント
const LoadingScreen = ({ message = 'ページを準備しています...' }: { message?: string }) => (
  <div className="flex flex-col items-center justify-center h-screen w-screen gap-4">
    <Spinner size={80} />
    <p className="text-muted-foreground">{message}</p>
  </div>
)

// サインイン後のコンテンツ（JWT同期フックを使用）
const AuthenticatedContent = () => {
  const { isReady } = useAuthSync()
  useRouteRestore()

  // オンライン復帰時の自動同期
  useSyncEngine()

  // 別端末変更の自動反映 (SSE)
  useSseSync()

  // Google でサインインした端末にパスキーの登録を 1 回だけ案内
  usePasskeyNudge(isReady)

  // 認証準備完了まではローディングを表示
  if (!isReady) {
    return <LoadingScreen message="接続しています..." />
  }

  return <AppRoutes routes={routes} />
}

// オフライン時のコンテンツ（認証なしでルートを表示）
const OfflineContent = () => {
  useRouteRestore()

  // オンラインでないと意味がないルートを除外
  const offlineRoutes = routes.filter(({ path }) => path !== PATHS.SIGN_OUT)

  return (
    <div className="h-screen w-screen">
      <AppRoutes routes={offlineRoutes} />
    </div>
  )
}

// オンライン時のコンテンツ（サインイン状態で出し分ける）
const OnlineAuthContent = () => {
  const isLoaded = useAuthStore((state) => state.isLoaded)
  const isSignedIn = useIsSignedIn()
  const [authTimedOut, setAuthTimedOut] = useState(false)
  const hasSignedInBefore = useAppStorePersisted((state) => state.hasSignedInBefore)

  // 保存済みセッションの復元が一定時間内に終わらない場合（期限切れトークンの更新が電波不良で進まない等）はオフラインフォールバック
  useEffect(() => {
    if (isLoaded) {
      setAuthTimedOut(false)
      return
    }
    const timer = setTimeout(() => {
      console.warn('[Auth] Session restore timed out, falling back to offline mode')
      setAuthTimedOut(true)
    }, 5_000)
    return () => clearTimeout(timer)
  }, [isLoaded])

  // タイムアウトかつ過去にサインイン済みならオフラインコンテンツ
  if (authTimedOut && hasSignedInBefore) {
    return <OfflineContent />
  }

  // セッション復元中は認証中画面を表示
  if (!isLoaded) {
    return <LoadingScreen message="認証中..." />
  }

  if (!isSignedIn) {
    return <SignInPage />
  }

  return (
    <div className="h-screen w-screen">
      <AuthenticatedContent />
    </div>
  )
}

// 認証状態に基づいてコンテンツを表示
const AuthContent = () => {
  const { shouldShowOfflineContent } = useOfflineAuth()
  // 一度オンライン認証まで到達したら、その後の一時的な回線断ではツリーを差し替えない。
  // 差し替えると配下が全て再マウントされ、認証ローディングを挟んで動画が再生し直される
  const [hasEnteredOnline, setHasEnteredOnline] = useState(false)

  // オフライン起動中にオンラインへ戻ったら、リロードせずそのままオンライン表示に切り替わる
  const showOffline = (!navigator.onLine || shouldShowOfflineContent) && !hasEnteredOnline

  useEffect(() => {
    if (showOffline) return
    setHasEnteredOnline(true)
  }, [showOffline])

  if (showOffline) {
    return <OfflineContent />
  }

  return <OnlineAuthContent />
}

function App() {
  const loading = useAppStore((state) => state.loading)
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const showDebugOverlay = useAppStore((state) => state.showDebugOverlay)

  return (
    <BrowserRouter>
      <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
        <AuthContent />
        <Toaster />
        <ToastHistoryRecorder />
        <ConfirmDialogHost />
        {hasFullAccess && showDebugOverlay && <DebugNetworkOverlay />}
        {loading > 0 && (
          <div className="fixed inset-0 z-9999 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4">
              <Spinner size={80} />
              <p className="text-muted-foreground">読み込み中...</p>
            </div>
          </div>
        )}
      </ThemeProvider>
    </BrowserRouter>
  )
}

export default App
